import {
  Field,
  FieldInputProps,
  FieldProps,
  FieldRenderProps,
} from 'react-final-form';

import { MultipleSuggestedInput as Input } from '@vk-hr-tek/ui/input';

import { compose } from '../validation';

import { MultipleSuggestedInputProps } from './MultipleSuggestedInput.types';

/**
 * Функция-обработчик изменения значения ввода.
 *
 * @param input - Объект с пропсами ввода из react-final-form.
 * @param onChange - Дополнительная функция-обработчик изменения значения.
 * @returns Функция, которая вызывает onChange из react-final-form и дополнительную onChange, если она передана.
 */
const handleChange =
  (
    input: FieldInputProps<
      string | string[] | number | number[] | undefined,
      HTMLElement
    >,
    onChange?: (value: string[] | undefined) => void,
  ) =>
  (value: string[] | undefined) => {
    input.onChange(value);

    if (onChange) {
      onChange(value);
    }
  };

/**
 * Компонент ввода с подсказками с множественным выбором.
 *
 * @public
 * @param type - Тип ввода: 'load' для загрузки с сервера, 'local' для локального списка.
 * @param name - Имя поля формы.
 * @param label - Текст метки для поля формы.
 * @param labelIcon - Иконка для метки.
 * @param onChange - Дополнительная функция-обработчик изменения значения.
 * @param placeholder - Плейсхолдер для поля ввода.
 * @param disabled - Флаг, указывающий, отключено ли поле.
 * @param required - Флаг, указывающий, является ли поле обязательным.
 * @param clearable - Флаг, указывающий, можно ли очистить поле.
 * @param loading - Флаг, указывающий, находится ли поле в состоянии загрузки.
 * @param tooltip - Тултип для поля формы.
 * @param helperText - Вспомогательный текст под полем.
 * @param renderOption - Функция для кастомного рендеринга опции.
 * @param limit - Максимальное количество отображаемых элементов.
 * @param minInputValueLength - Минимальная длина введённого значения для отображения подсказок.
 * @param minInputValueLengthForRequest - Минимальная длина введённого значения для отправки запроса.
 * @param validate - Правила валидации.
 * @param validationWithoutTouch - Флаг, указывающий, нужно ли показывать ошибку без касания поля.
 * @param validationWhenDisabled - Флаг, указывающий, нужно ли валидировать поле, когда оно отключено.
 * @param rest - Остальные пропсы, передаваемые в {@link Field}.
 * @returns Компонент Field, который рендерит кастомный компонент ввода Input.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link MultipleSuggestedInputProps}
 */
export const MultipleSuggestedInput = (
  props: MultipleSuggestedInputProps &
    FieldProps<string[] | undefined, FieldRenderProps<string[] | undefined>>,
) => {
  const {
    type,
    name,
    label,
    labelIcon,
    onChange,
    placeholder,
    disabled = false,
    required = false,
    clearable = false,
    loading = false,
    tooltip,
    helperText,
    renderOption,
    limit = 50,
    minInputValueLength = 1,
    minInputValueLengthForRequest = minInputValueLength,
    validate,
    validationWithoutTouch = false,
    validationWhenDisabled = false,
    ...rest
  } = props;

  return (
    <Field
      name={name}
      {...rest}
      validate={Array.isArray(validate) ? compose(validate) : validate}
    >
      {({ input, meta }) => {
        /**
         * Флаг для отображения ошибки в зависимости от состояния disabled.
         */
        const errorDisabled = disabled || !validationWhenDisabled;

        /**
         * Флаг для отображения ошибки.
         */
        const renderError =
          !errorDisabled && validationWithoutTouch
            ? !!input.value && !!meta.error
            : meta.touched && !!meta.error;

        const baseInputProps = {
          ...input,
          label,
          labelIcon,
          onChange: handleChange(input, onChange),
          placeholder,
          disabled,
          required,
          clearable,
          loading,
          tooltip,
          error: renderError,
          helperText: (renderError && meta.error) || helperText,
          renderOption,
          limit,
          minInputValueLength,
          minInputValueLengthForRequest,
        };

        return props.type === 'load' ? (
          <Input
            {...baseInputProps}
            type="load"
            loadItems={props.loadItems}
            mask={props.mask}
          />
        ) : (
          <Input
            {...baseInputProps}
            type="local"
            externalItems={props.externalItems}
          />
        );
      }}
    </Field>
  );
};
